import { FadeIn } from "@xola/ui-kit";
import clsx from "clsx";
import { sumBy } from "lodash";
import React from "react";
import { ExperiencePurchaseItem, LineItemType, Package, PartnerPackage, Purchase } from "types";
import { useResource } from "../../hooks/useResource";
import { CreatePurchaseStep, useCreatePurchaseStore } from "../../stores/createPurchaseStore";
import { Breakdown } from "../Common/Breakdown";
import { ProductImage } from "../Common/ProductImage";
import { BreakdownLineItem } from "../PurchaseModification/PaymentBreakdown";
import { Balance, GrandTotal, PartnerFee } from "./CreateExperiencePurchaseSidebar";
import { CreatePurchaseApplyCode } from "./CreatePurchaseApplyCode";
import { CreatePurchaseBreakdown } from "./CreatePurchaseBreakdown";
import { CreatePurchasePackageBreakdown } from "./CreatePurchasePackageBreakdown";
import { CreatePurchaseSidebarSkeleton } from "./CreatePurchaseSidebarSkeleton";
import { getPurchaseItemsHavingId } from "./helpers/PurchaseCreateHelpers";
import { PackageSummary } from "./PackageSummary";
import { PurchaseItemSummary } from "./PurchaseItemSummary";
import { usePurchaseResponseStore } from "./store/usePurchaseResponseStore";

export const CreatePackagePurchaseSidebar = () => {
    const purchase = usePurchaseResponseStore((state) => state.purchase as Purchase);

    const activeItemIndex = useCreatePurchaseStore((state) => state.activeItemIndex);
    const step = useCreatePurchaseStore((state) => state.step);
    const showPayments = useCreatePurchaseStore((state) => !state.isWaitListPurchase);
    // This state is used to disable the cancel and submit buttons in the Coupon Restrictions modal
    // so that the user cannot request multiple actions at the same time.
    const setActiveItemIndex = useCreatePurchaseStore((state) => state.actions.setActiveItemIndex);
    const setPurchase = useCreatePurchaseStore((state) => state.actions.setPurchase);

    const packageId = useCreatePurchaseStore((state) => state.getPurchasePackageId());
    const isVariableDemographicsPackage = useCreatePurchaseStore((state) => state.isVariableDemographicsPackage());
    const { data: pkg } = useResource<Package | PartnerPackage>(["products", packageId], null, {
        enabled: !!isVariableDemographicsPackage,
    });
    const isCheckout = step === CreatePurchaseStep.CHECKOUT;

    if (!purchase.id) {
        return <CreatePurchaseSidebarSkeleton />;
    }

    const handleEditClick = (index: number) => () => {
        setPurchase((draft) => getPurchaseItemsHavingId(draft));

        setActiveItemIndex(index);
        useCreatePurchaseStore.setState({ step: CreatePurchaseStep.SELECT_DATE });

        const createPurchaseScreenLayout = document.querySelector(`#columnLayoutFill`);
        if (createPurchaseScreenLayout) {
            (createPurchaseScreenLayout as HTMLElement).scroll(0, 0);
        }
    };

    const paymentLineItems = purchase.lineItems.filter((lineItem) => {
        return lineItem.type === LineItemType.PAYMENT || lineItem.type === LineItemType.AFFILIATE_DEPOSIT;
    });

    const hasPayments = paymentLineItems.length > 0;

    const sidebarClasses = isVariableDemographicsPackage
        ? `space-y-0.5 rounded border ${isCheckout ? "border-gray" : "border-primary bg-primary-lighter"} p-1`
        : "space-y-4";

    return (
        <div className="col-span-6 shrink-0 space-y-6 bg-gray-lighter 2xl:col-span-5">
            <div className="sticky top-0 space-y-6 px-10 py-7">
                <h3>Purchase Summary</h3>

                <div className={clsx("flex flex-col", sidebarClasses)}>
                    {isVariableDemographicsPackage && pkg && (
                        <div className="flex items-center space-x-1 pb-1">
                            <div className="shrink-0">
                                <ProductImage product={pkg} size="small" className="!mr-1" />
                            </div>

                            <h4 className="font-bold">{pkg.name}</h4>
                        </div>
                    )}

                    {packageId && !isVariableDemographicsPackage && (
                        <PackageSummary key={packageId} isActive purchase={purchase}>
                            <CreatePurchasePackageBreakdown purchase={purchase} packageId={packageId} />
                        </PackageSummary>
                    )}
                    {isVariableDemographicsPackage
                        ? purchase?.items.map((item, index) => {
                              const isActive = index === activeItemIndex;

                              return (
                                  <PurchaseItemSummary
                                      key={item.id}
                                      isActive={isActive}
                                      canEdit={step === CreatePurchaseStep.CHECKOUT}
                                      itemIndex={index}
                                      item={item as ExperiencePurchaseItem}
                                      onEditClick={handleEditClick(index)}
                                  >
                                      <CreatePurchaseBreakdown item={item} />
                                  </PurchaseItemSummary>
                              );
                          })
                        : null}
                </div>

                <PartnerFee />
                <GrandTotal />

                {showPayments && hasPayments ? (
                    <div
                        className="min-h-[150px] border-t border-black/10 px-0 py-6"
                        data-testid="create-purchase-sidebar-payment-summary"
                    >
                        <h3 className="mb-4">Payment Summary</h3>

                        <FadeIn>
                            <Breakdown currency={purchase.currency}>
                                {paymentLineItems.map((lineItem) => (
                                    <BreakdownLineItem key={lineItem.id} purchase={purchase} lineItem={lineItem} />
                                ))}

                                <Breakdown.Separator />
                                <Breakdown.SubtotalItem
                                    info="Total"
                                    value={Math.abs(sumBy(paymentLineItems, "amount"))}
                                />
                            </Breakdown>
                        </FadeIn>
                    </div>
                ) : null}

                <CreatePurchaseApplyCode />

                {showPayments && hasPayments ? (
                    <div className="border-t border-black/10 pt-6">
                        <Balance />
                    </div>
                ) : null}
            </div>
        </div>
    );
};
